import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  completenessTone,
  fieldCompleteness,
  formatProfileMissingSummary,
} from "../../utils/profileCompleteness";
import EmployeeHistoryView from "../common/EmployeeHistoryView";
import "./HrProfileCompletion.css";

export default function HrProfileCompletion({ userRole }) {
  const { getAllEmployees, loading: dataLoading } = useData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("incomplete");
  const [selected, setSelected] = useState(null);

  const employees = useMemo(
    () =>
      getAllEmployees().filter(
        (emp) => emp.status !== "terminated" && emp.status !== "inactive",
      ),
    [getAllEmployees],
  );

  const summary = useMemo(() => {
    const total = employees.length;
    const complete = employees.filter(
      (emp) => fieldCompleteness(emp.profileCompleteness).isComplete,
    ).length;
    const incomplete = total - complete;
    const average =
      total === 0
        ? 0
        : Math.round(
            employees.reduce(
              (sum, emp) =>
                sum + fieldCompleteness(emp.profileCompleteness).percent,
              0,
            ) / total,
          );
    return { total, complete, incomplete, average };
  }, [employees]);

  const filtered = useMemo(() => {
    const term = search.trim();
    return employees
      .filter((emp) => {
        const { percent, isComplete } = fieldCompleteness(
          emp.profileCompleteness,
        );
        if (statusFilter === "complete" && !isComplete) return false;
        if (statusFilter === "incomplete" && isComplete) return false;
        if (statusFilter === "low" && percent >= 40) return false;
        if (term) {
          const haystack = `${emp.name} ${emp.code} ${emp.jobTitle || ""}`;
          if (!haystack.includes(term)) return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          fieldCompleteness(a.profileCompleteness).percent -
          fieldCompleteness(b.profileCompleteness).percent,
      );
  }, [employees, search, statusFilter]);

  if (selected) {
    return (
      <EmployeeHistoryView
        employee={selected}
        userRole={userRole}
        onBack={() => setSelected(null)}
        initialTab="profile"
      />
    );
  }

  return (
    <div className="hr-profile-completion" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">اكتمال ملفات الموظفين</h1>
        <p className="ess-home-subtitle">
          النسبة للبيانات الشخصية التي يدخلها الموظف. المستندات الناقصة تظهر في
          عمود الناقص ولا تخفّض النسبة.
        </p>
      </div>

      <div className="hpc-stats">
        <div className="hpc-stat">
          <strong>{summary.total}</strong>
          <span>موظف نشط</span>
        </div>
        <div className="hpc-stat hpc-stat-ok">
          <strong>{summary.complete}</strong>
          <span>ملف مكتمل</span>
        </div>
        <div className="hpc-stat hpc-stat-warn">
          <strong>{summary.incomplete}</strong>
          <span>ملف ناقص</span>
        </div>
        <div className="hpc-stat">
          <strong>{summary.average}%</strong>
          <span>متوسط الاكتمال</span>
        </div>
      </div>

      <div className="hpc-filters">
        <input
          className="hpc-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود..."
        />
        <select
          className="hpc-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="incomplete">الناقص فقط</option>
          <option value="complete">المكتمل فقط</option>
          <option value="low">أقل من 40%</option>
          <option value="all">الكل</option>
        </select>
      </div>

      <div className="hpc-table-card">
        {dataLoading ? (
          <p className="hpc-empty">جاري تحميل الموظفين...</p>
        ) : (
          <table className="hpc-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>الإدارة / الفرع</th>
                <th>الاكتمال</th>
                <th>الناقص</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp) => {
                const { percent } = fieldCompleteness(emp.profileCompleteness);
                const missing = formatProfileMissingSummary(
                  emp.profileCompleteness,
                );
                return (
                  <tr key={emp.id} onClick={() => setSelected(emp)}>
                    <td data-label="الموظف">
                      <div className="hpc-name">{emp.name}</div>
                      <div className="hpc-meta">
                        {emp.code} · {emp.jobTitle}
                      </div>
                    </td>
                    <td data-label="الإدارة / الفرع">
                      {emp.departmentName || "—"}
                      {emp.branchName ? ` · ${emp.branchName}` : ""}
                    </td>
                    <td data-label="الاكتمال">
                      <div className={`hpc-bar hpc-bar--${completenessTone(percent)}`}>
                        <span>{percent}%</span>
                        <div className="hpc-bar-track">
                          <div
                            className="hpc-bar-fill"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td data-label="الناقص">
                      {missing.length === 0 ? (
                        <span className="hpc-ok">مكتمل</span>
                      ) : (
                        missing.slice(0, 4).join(" · ") +
                        (missing.length > 4 ? ` +${missing.length - 4}` : "")
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!dataLoading && filtered.length === 0 && (
          <p className="hpc-empty">لا يوجد موظفون مطابقون للفلتر</p>
        )}
      </div>
    </div>
  );
}
